import * as yup from "yup";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  BusinessEnglishObjective,
  OneOnOneLessonType,
  SpeakingClassLevel,
} from "../gql/graphql.ts";
import { Col } from "../UI/Layout/Col.tsx";
import { Row } from "../UI/Layout/Row.tsx";
import { TextInput } from "../UI/TextInput.tsx";
import { InputWithLabel } from "../UI/InputWithLabel.tsx";
import { Select } from "@normasteaching/norma-ui";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";
import { getAllSpeakingClassLevelOptions } from "../Topic/getAllSpeakingClassLevelOptions.ts";
import { useAllUserGroups } from "../UserGroup/useAllUserGroups.ts";

export type OneOnOneAssignmentPdfFormInputs = {
  assignmentNumber: number;
  level: SpeakingClassLevel;
  oneOnOneLessonType: OneOnOneLessonType;
  pdfUrl: string;
  userGroupId: string;
  businessEnglishObjective?: BusinessEnglishObjective;
};

type Props = {
  defaultValues?: Partial<OneOnOneAssignmentPdfFormInputs>;
  onSave: (data: OneOnOneAssignmentPdfFormInputs) => Promise<void>;
  disabledMap?: Partial<Record<keyof OneOnOneAssignmentPdfFormInputs, boolean>>;
};

const assignmentNumberOptions = ["1", "2", "3"].map((n) => ({
  id: n,
  name: `Assignment ${n}`,
}));

const lessonTypeOptions = Object.values(OneOnOneLessonType).map((v) => ({
  id: v,
  name: v,
}));

const businessEnglishObjectiveOptions = Object.values(
  BusinessEnglishObjective,
).map((v) => ({ id: v, name: v }));

const schema = yup
  .object({
    assignmentNumber: yup
      .number()
      .oneOf([1, 2, 3], "Il numero assignment deve essere 1, 2 o 3")
      .required("Specifica il numero dell'assignment"),
    level: yup
      .mixed<SpeakingClassLevel>()
      .oneOf(Object.values(SpeakingClassLevel))
      .required("Specifica un livello"),
    oneOnOneLessonType: yup
      .mixed<OneOnOneLessonType>()
      .oneOf(Object.values(OneOnOneLessonType))
      .required("Specifica il tipo di lezione 1on1"),
    pdfUrl: yup
      .string()
      .url("Specifica un URL valido")
      .required("Specifica un URL per il PDF"),
    userGroupId: yup.string().required("Seleziona un user group"),
    businessEnglishObjective: yup
      .mixed<BusinessEnglishObjective>()
      .oneOf([...Object.values(BusinessEnglishObjective), undefined])
      .when("oneOnOneLessonType", {
        is: OneOnOneLessonType.BusinessEnglish,
        then: (s) => s.required("Specifica l'obiettivo Business English"),
        otherwise: (s) => s.optional(),
      }),
  })
  .required();

export function OneOnOneAssignmentPdfForm({
  defaultValues,
  onSave,
  disabledMap,
}: Props) {
  const levelOptions = getAllSpeakingClassLevelOptions();
  const { userGroups } = useAllUserGroups();
  const userGroupOptions = (userGroups ?? []).map((ug) => ({
    id: ug.id,
    name: `${ug.name} (${ug.slug})`,
  }));

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<OneOnOneAssignmentPdfFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      assignmentNumber: 1,
      level: levelOptions[0]?.id,
      oneOnOneLessonType: OneOnOneLessonType.GeneralEnglish,
      ...(defaultValues ?? {}),
    } as OneOnOneAssignmentPdfFormInputs,
  });

  const selectedLessonType = useWatch({ control, name: "oneOnOneLessonType" });
  const isBusinessEnglish =
    selectedLessonType === OneOnOneLessonType.BusinessEnglish;

  const { withLoading, loading } = useWithLoading();
  const onSubmit: SubmitHandler<OneOnOneAssignmentPdfFormInputs> = async (
    data,
  ) => {
    await withLoading(async () => onSave(data));
  };

  return (
    <form>
      <Col>
        <Row>
          <InputWithLabel label={"Level"}>
            <Controller
              control={control}
              render={({ field }) => (
                <Select<SpeakingClassLevel>
                  disabled={disabledMap?.level}
                  errorMessage={errors.level?.message as string}
                  onChange={field.onChange}
                  value={field.value}
                  options={levelOptions}
                />
              )}
              name={"level"}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label={"User group"}>
            <Controller
              control={control}
              name={"userGroupId"}
              render={({ field }) => (
                <Select<string>
                  disabled={disabledMap?.userGroupId}
                  errorMessage={errors.userGroupId?.message}
                  onChange={field.onChange}
                  value={field.value}
                  options={userGroupOptions}
                />
              )}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label={"Lesson type"}>
            <Controller
              control={control}
              name={"oneOnOneLessonType"}
              render={({ field }) => (
                <Select<OneOnOneLessonType>
                  disabled={disabledMap?.oneOnOneLessonType}
                  errorMessage={errors.oneOnOneLessonType?.message as string}
                  onChange={field.onChange}
                  value={field.value}
                  options={lessonTypeOptions}
                />
              )}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label={"Assignment number"}>
            <Controller
              control={control}
              name={"assignmentNumber"}
              render={({ field }) => (
                <Select<string>
                  disabled={disabledMap?.assignmentNumber}
                  errorMessage={errors.assignmentNumber?.message as string}
                  onChange={(v) => field.onChange(parseInt(v ?? "1", 10))}
                  value={String(field.value)}
                  options={assignmentNumberOptions}
                />
              )}
            />
          </InputWithLabel>
        </Row>
        {isBusinessEnglish && (
          <Row>
            <InputWithLabel label={"Business English Objective"}>
              <Controller
                control={control}
                name={"businessEnglishObjective"}
                render={({ field }) => (
                  <Select<BusinessEnglishObjective>
                    disabled={disabledMap?.businessEnglishObjective}
                    errorMessage={
                      errors.businessEnglishObjective?.message as string
                    }
                    onChange={field.onChange}
                    value={field.value ?? undefined}
                    options={businessEnglishObjectiveOptions}
                  />
                )}
              />
            </InputWithLabel>
          </Row>
        )}
        <Row>
          <InputWithLabel label={"PDF url"}>
            <Controller
              control={control}
              name={"pdfUrl"}
              render={({ field }) => (
                <TextInput errorMessage={errors.pdfUrl?.message} {...field} />
              )}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <div className={"flex w-full justify-end"}>
            <PrimaryButton
              loading={loading}
              onClick={handleSubmit(onSubmit)}
              label={"Save"}
            />
          </div>
        </Row>
      </Col>
    </form>
  );
}
